/**
 * Communication Templates Hub — tabbed shell over the org's own templates
 * (CommunicationTemplates.tsx) and the universal, cross-org Template
 * Library (TemplateLibraryGallery.tsx). Both sidebar entries route here;
 * only the initialTab differs.
 *
 * Post-import handoff: when a user clicks "Import to my org" inside the
 * Universal Library tab, the gallery fires onCloned -> we flip to the
 * My Org tab, bump a refreshKey that re-fetches the org's template list,
 * and show a green banner naming the just-imported template.
 */
import React, { useState } from 'react';
import CommunicationTemplates from '../admin/CommunicationTemplates';
import TemplateLibraryGallery from './TemplateLibraryGallery';

export type HubTab = 'my-org' | 'universal';

interface Props {
  cboId: number;
  currentUserId: number;
  initialTab?: HubTab;
  onBack?: () => void;
}

const TAB_LABELS: Record<HubTab, string> = {
  'my-org': 'My Org Templates',
  'universal': 'Universal Library',
};

const CommunicationTemplatesHub: React.FC<Props> = ({
  cboId,
  currentUserId,
  initialTab = 'my-org',
  onBack,
}) => {
  const [activeTab, setActiveTab] = useState<HubTab>(initialTab);
  const [refreshKey, setRefreshKey] = useState(0);
  const [importedBanner, setImportedBanner] = useState<{ name: string } | null>(null);

  const handleImported = (_newTemplateId: number, source: { name: string }) => {
    setActiveTab('my-org');
    setRefreshKey(k => k + 1);
    setImportedBanner({ name: source.name });
    window.setTimeout(() => setImportedBanner(null), 10000);
  };

  return (
    <div>
      <div className="px-6 pt-6">
        <p className="max-w-3xl text-[13px] text-ink-500">
          <strong>My Org Templates</strong> are owned and editable by your organization.
          The <strong>Universal Library</strong> is a shared, cross-org catalog — preview any
          entry and click <strong>Import to my org</strong> to copy it into your editable
          library.
        </p>

        <div className="mt-4 flex gap-1 border-b border-v2border">
          {(['my-org', 'universal'] as HubTab[]).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium transition ${
                activeTab === tab
                  ? 'border-v2accent text-v2accent-ink'
                  : 'border-transparent text-ink-500 hover:text-ink-700'
              }`}
            >
              {TAB_LABELS[tab]}
              {tab === 'universal' && (
                <span className="ml-2 rounded-full bg-paper-2 px-1.5 py-0.5 text-[9px] font-bold uppercase text-ink-700">
                  cross-org
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {importedBanner && activeTab === 'my-org' && (
        <div className="mx-6 mt-4 flex items-start gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
          <div className="flex-1">
            <strong>Imported &quot;{importedBanner.name}&quot;</strong> into your org&apos;s
            templates. Edit it below, then launch a campaign when ready.
          </div>
          <button
            onClick={() => setImportedBanner(null)}
            className="text-xs underline hover:text-emerald-700"
          >
            dismiss
          </button>
        </div>
      )}

      {activeTab === 'my-org' ? (
        <CommunicationTemplates onBack={onBack} refreshKey={refreshKey} />
      ) : (
        <div className="p-6">
          <TemplateLibraryGallery
            cboId={cboId}
            currentUserId={currentUserId}
            onCloned={handleImported}
          />
        </div>
      )}
    </div>
  );
};

export default CommunicationTemplatesHub;
